import { create } from "zustand";
import { persist } from "zustand/middleware";
import { GroupedAvailability } from "../../../components/SupplierInventorySearch/utils/typesenseUtils";

interface PartAvailabilityState {
  availabilityData: Record<string, GroupedAvailability[]>;
  lastFetchTimes: Record<string, string>;
  availabilityColumnsVisible: Record<string, boolean>;
  partsChecked: Record<string, any[]>;

  setAvailabilityData: (orderId: string, data: GroupedAvailability[]) => void;
  setLastFetchTime: (orderId: string, timestamp: Date) => void;
  setAvailabilityColumnVisible: (orderId: string, visible: boolean) => void;
  setPartsChecked: (orderId: string, parts: any[]) => void;

  getAvailabilityData: (orderId: string) => GroupedAvailability[] | undefined;
  getLastFetchTime: (orderId: string) => Date | undefined;
  isAvailabilityColumnVisible: (orderId: string) => boolean;
  getPartsChecked: (orderId: string) => any[] | undefined;

  clearOrderData: (orderId: string) => void;
}

export const usePartAvailabilityStore = create<PartAvailabilityState>()(
  persist(
    (set, get) => ({
      availabilityData: {},
      lastFetchTimes: {},
      availabilityColumnsVisible: {},
      partsChecked: {},

      setAvailabilityData: (orderId: string, data: GroupedAvailability[]) => {
        set((state) => ({
          availabilityData: {
            ...state.availabilityData,
            [orderId]: data,
          },
        }));
      },

      setLastFetchTime: (orderId: string, timestamp: Date) => {
        set((state) => ({
          lastFetchTimes: {
            ...state.lastFetchTimes,
            [orderId]: timestamp.toISOString(),
          },
        }));
      },

      setAvailabilityColumnVisible: (orderId: string, visible: boolean) => {
        set((state) => ({
          availabilityColumnsVisible: {
            ...state.availabilityColumnsVisible,
            [orderId]: visible,
          },
        }));
      },

      setPartsChecked: (orderId: string, parts: any[]) => {
        set((state) => ({
          partsChecked: {
            ...state.partsChecked,
            [orderId]: parts,
          },
        }));
      },

      getAvailabilityData: (orderId: string) => {
        return get().availabilityData[orderId];
      },

      getLastFetchTime: (orderId: string) => {
        const timestamp = get().lastFetchTimes[orderId];
        return timestamp ? new Date(timestamp) : undefined;
      },

      isAvailabilityColumnVisible: (orderId: string) => {
        return get().availabilityColumnsVisible[orderId] || false;
      },

      getPartsChecked: (orderId: string) => {
        return get().partsChecked[orderId];
      },

      clearOrderData: (orderId: string) => {
        set((state) => {
          const newAvailabilityData = { ...state.availabilityData };
          const newLastFetchTimes = { ...state.lastFetchTimes };
          const newAvailabilityColumnsVisible = {
            ...state.availabilityColumnsVisible,
          };
          const newPartsChecked = { ...state.partsChecked };

          delete newAvailabilityData[orderId];
          delete newLastFetchTimes[orderId];
          delete newAvailabilityColumnsVisible[orderId];
          delete newPartsChecked[orderId];

          return {
            availabilityData: newAvailabilityData,
            lastFetchTimes: newLastFetchTimes,
            availabilityColumnsVisible: newAvailabilityColumnsVisible,
            partsChecked: newPartsChecked,
          };
        });
      },
    }),
    {
      name: "part-availability-store",
    }
  )
);
